import {
  loginResponseSchema,
  messageResponseSchema,
  totpEnableRequestSchema,
  type LoginRequest,
  type TotpEnableRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { clearPushPromptLaterDismissed } from '@/platform/features/notifications/push/push-prompt-preferences';
import { unregisterThisBrowserPushDevice } from '@/platform/features/notifications/push/register-push';
import { browserTypographyStorage, clearTypography } from '@/platform/theme/typography';

import { meQueryKey } from './use-me';

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) =>
      apiFetch('/auth/login', loginResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (result) => {
      if (result.status === 'ok') {
        clearPushPromptLaterDismissed();
        queryClient.setQueryData(meQueryKey, result.user);
      }
    },
  });
}

export function useEnableTotp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TotpEnableRequest) =>
      apiFetch('/auth/2fa/enable', loginResponseSchema, {
        method: 'POST',
        body: JSON.stringify(totpEnableRequestSchema.parse(body)),
      }),
    onSuccess: (result) => {
      if (result.status === 'ok') {
        clearPushPromptLaterDismissed();
        queryClient.setQueryData(meQueryKey, result.user);
      }
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await unregisterThisBrowserPushDevice();
      } catch {
        // Push cleanup must not block logout.
      }
      return apiFetch('/auth/logout', messageResponseSchema, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
    onSuccess: async () => {
      clearTypography(browserTypographyStorage());
      queryClient.setQueryData(meQueryKey, null);
      await queryClient.invalidateQueries();
    },
  });
}
